import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import ReactMarkdown from "react-markdown";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/chat")({
  component: ChatPage,
});

const suggestions = [
  "I'm feeling overwhelmed by work",
  "I had a panic attack today",
  "Why can't I sleep at night?",
  "Help me through this moment",
];

function ChatPage() {
  const { messages, sendMessage, status } = useChat({
    transport: new DefaultChatTransport({ api: "/api/chat" }),
  });
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, status]);
  useEffect(() => { inputRef.current?.focus(); }, [status]);

  const submit = async (text: string) => {
    const t = text.trim();
    if (!t || status === "submitted" || status === "streaming") return;
    setInput("");
    await sendMessage({ text: t });
  };

  return (
    <div className="space-y-4 -mt-2">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-primary to-accent glow-primary flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl">Sage</h1>
          <p className="text-xs text-muted-foreground">AI companion · always anonymous · not a replacement for emergency care</p>
        </div>
      </div>

      <div className="glass rounded-3xl p-6 min-h-[55vh] flex flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto max-h-[60vh] pr-2">
          {messages.length === 0 && (
            <div className="space-y-4 py-8">
              <p className="text-center text-muted-foreground text-sm">Whatever you're carrying, you can put some of it down here.</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => submit(s)} className="text-xs px-3 py-1.5 rounded-full bg-secondary/60 hover:bg-secondary text-muted-foreground hover:text-foreground transition">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => {
            const text = m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
            const mine = m.role === "user";
            return (
              <div key={m.id} className={mine ? "flex justify-end" : "flex justify-start"}>
                <div className={mine ? "max-w-[80%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm" : "max-w-[85%] text-sm prose prose-invert prose-p:my-2 prose-sm"}>
                  {mine ? text : <ReactMarkdown>{text}</ReactMarkdown>}
                </div>
              </div>
            );
          })}
          {(status === "submitted") && (
            <div className="text-sm text-muted-foreground italic animate-pulse">Sage is thinking…</div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); submit(input); }} className="mt-4 flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(input); } }}
            placeholder="Tell Sage what's on your mind…"
            rows={2}
            className="resize-none bg-secondary/50 border-border/60"
          />
          <Button type="submit" size="icon" disabled={!input.trim() || status === "submitted" || status === "streaming"} className="h-10 w-10 shrink-0 glow-primary">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
